import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { claimOnce } from "@/lib/fanzone";
import { toast } from "sonner";
import {
  Trophy,
  Lightbulb,
  Send,
  RotateCcw,
  CheckCircle2,
  XCircle,
  Eye,
  Sparkles,
  Calendar as CalIcon,
  Share2,
} from "lucide-react";
import StreakBadge from "@/components/StreakBadge";

const HIGH_KEY = "titans_gtt_high_v1";
const DATE_KEY = "titans_gtt_date_v1";

export default function GuessTheTitan() {
  const [date, setDate] = useState("");
  const [clues, setClues] = useState([]);
  const [cluesTotal, setCluesTotal] = useState(5);
  const [used, setUsed] = useState(1);
  const [scoreCurve, setScoreCurve] = useState([100, 80, 60, 40, 20]);
  const [guess, setGuess] = useState("");
  const [result, setResult] = useState(null); // {correct, score, answer?}
  const [revealedAnswer, setRevealedAnswer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [high, setHigh] = useState(() => parseInt(localStorage.getItem(HIGH_KEY) || "0", 10));

  const load = useCallback(async (clueCount = 1) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/guess-the-titan/daily?clues=${clueCount}`);
      setDate(data.date);
      setClues(data.clues || []);
      setCluesTotal(data.clues_total || 5);
      setScoreCurve(data.score_curve || [100, 80, 60, 40, 20]);
      setUsed(data.clues_visible || clueCount);
    } catch (e) {
      console.error("GuessTheTitan load failed:", e);
      toast.error("Couldn't load today's challenge");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(1); }, [load]);

  useEffect(() => {
    if (result && date && (result.correct || result.gave_up)) {
      claimOnce("guess_titan", date, { correct: !!result.correct, score: result.score || 0, clues_used: used });
    }
  }, [result, date, used]);

  // Reset the round each new UTC day
  useEffect(() => {
    if (!date) return;
    const stored = localStorage.getItem(DATE_KEY);
    if (stored !== date) {
      localStorage.setItem(DATE_KEY, date);
      setResult(null);
      setRevealedAnswer(null);
      setGuess("");
    }
  }, [date]);

  const revealNext = async () => {
    if (used >= cluesTotal || result) return;
    await load(used + 1);
  };

  const submit = async (e) => {
    if (e) e.preventDefault();
    if (!guess.trim() || result) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/guess-the-titan/guess", { guess: guess.trim(), clues_used: used, date });
      setResult(data);
      if (data.correct) {
        toast.success(`Nailed it — +${data.score} points`);
        if (data.score > high) {
          localStorage.setItem(HIGH_KEY, String(data.score));
          setHigh(data.score);
        }
      } else {
        toast.error("Not quite — try another guess or peek at the next clue");
      }
    } catch (e) {
      console.error("GuessTheTitan submit failed:", e);
      toast.error("Couldn't submit guess");
    } finally {
      setSubmitting(false);
    }
  };

  const giveUp = async () => {
    if (result) return;
    try {
      const { data } = await api.get("/guess-the-titan/daily?reveal=1");
      setRevealedAnswer(data.answer);
      setResult({ correct: false, score: 0, answer: data.answer, gave_up: true });
    } catch (e) {
      console.error("GuessTheTitan giveup failed:", e);
    }
  };

  const reset = () => {
    setResult(null);
    setRevealedAnswer(null);
    setGuess("");
    load(1);
  };

  const potentialPoints = useMemo(() => scoreCurve[Math.max(0, Math.min(used - 1, scoreCurve.length - 1))], [scoreCurve, used]);
  const finalAnswer = result?.answer || revealedAnswer;

  return (
    <div data-testid="guess-the-titan" className="titans-card p-6 md:p-8">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div>
          <div className="font-cond uppercase tracking-[0.25em] text-[10px] text-titans-blue inline-flex items-center gap-1.5">
            <Sparkles size={12} /> Daily Challenge · Guess the Titan
          </div>
          <h3 className="font-display text-2xl md:text-3xl uppercase tracking-wide mt-2">
            Who am I?
          </h3>
          <p className="text-titans-silver text-sm font-body mt-1">
            5 progressive clues — fewer used, more points. Past or present Titan / Oiler.
          </p>
        </div>
        <div className="text-right">
          <div className="text-titans-silver text-[10px] font-cond uppercase tracking-wider inline-flex items-center gap-1">
            <CalIcon size={10} /> {date || "—"}
          </div>
          <div className="font-display text-3xl text-white leading-none mt-1">{high}</div>
          <div className="text-titans-silver text-[10px] font-cond uppercase tracking-wider">High Score</div>
          <div className="mt-2 flex justify-end">
            <StreakBadge
              storageKey="titans_gtt_streak_v1"
              triggerKey={result && (result.correct || result.gave_up) ? date : null}
            />
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-titans-silver">Loading today&apos;s challenge…</div>
      ) : (
        <>
          {/* Score meter row */}
          <div className="grid grid-cols-5 gap-1 mb-5" data-testid="gtt-score-pips">
            {scoreCurve.map((pts, i) => (
              <div
                key={i}
                className={`p-2 text-center border ${
                  i + 1 < used
                    ? "border-titans-silver/30 text-titans-muted"
                    : i + 1 === used && !result
                      ? "border-titans-red bg-titans-red/20 text-white"
                      : "border-titans-blue/30 text-titans-silver"
                }`}
              >
                <div className="font-cond uppercase tracking-wider text-[9px]">Clue {i + 1}</div>
                <div className="font-display text-lg leading-none mt-0.5">{pts}</div>
              </div>
            ))}
          </div>

          {/* Clues */}
          <div className="space-y-2 mb-5" data-testid="gtt-clues">
            {clues.map((c, i) => (
              <div
                key={i}
                className="bg-titans-void border-l-4 border-titans-blue p-3 flex items-start gap-3"
                data-testid={`gtt-clue-${i + 1}`}
              >
                <Lightbulb size={14} className="text-titans-blue mt-0.5 flex-shrink-0" />
                <div>
                  <div className="font-cond uppercase tracking-wider text-[10px] text-titans-blue">Clue {i + 1}</div>
                  <div className="text-white font-body text-sm md:text-base mt-0.5">{c}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Result / Answer card */}
          {finalAnswer && (
            <div
              className={`p-5 mb-5 border-l-4 ${result?.correct ? "border-green-500 bg-green-500/10" : "border-titans-red bg-titans-red/10"}`}
              data-testid="gtt-result"
            >
              <div className="flex items-center gap-2 mb-2">
                {result?.correct ? (
                  <>
                    <CheckCircle2 size={18} className="text-green-400" />
                    <span className="font-cond uppercase tracking-wider text-sm text-green-400">
                      Correct · +{result.score} points
                    </span>
                  </>
                ) : (
                  <>
                    <Eye size={18} className="text-titans-red" />
                    <span className="font-cond uppercase tracking-wider text-sm text-titans-red">
                      The answer was…
                    </span>
                  </>
                )}
              </div>
              <div className="font-display text-3xl uppercase">{finalAnswer.name}</div>
              <div className="font-cond uppercase tracking-wider text-xs text-titans-silver mt-1">
                {finalAnswer.position} · {finalAnswer.years}
              </div>
              {(finalAnswer.former_teams || []).length > 0 && (
                <div className="text-titans-silver text-xs mt-2">
                  Also played for: {finalAnswer.former_teams.join(", ")}
                </div>
              )}

              {/* Share-your-streak (Twitter intent) — fans love to flex */}
              {(() => {
                const streak = parseInt(localStorage.getItem("titans_gtt_streak_v1") || "0", 10);
                const cluesUsedText = `${used} clue${used === 1 ? "" : "s"}`;
                const tweet = result?.correct
                  ? `I just guessed today's #GuessTheTitan in ${cluesUsedText} for ${result.score} pts${streak > 0 ? ` 🔥 ${streak}-day streak` : ""}.`
                  : `Today's #GuessTheTitan stumped me — answer was ${finalAnswer.name}. Think you can do better?`;
                const shareUrl = "https://thetitanstandard.com/community?tab=guessthetitan";
                const href = `https://twitter.com/intent/tweet?text=${encodeURIComponent(tweet)}&url=${encodeURIComponent(shareUrl)}`;
                return (
                  <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="gtt-share-streak"
                    className="mt-4 inline-flex items-center gap-2 bg-[#1DA1F2] hover:bg-[#0d8bd9] text-white px-4 py-2 font-cond uppercase tracking-wider text-xs"
                  >
                    <Share2 size={12} />
                    {result?.correct && streak > 1 ? `Share My ${streak}-Day Streak` : "Share to X / Twitter"}
                  </a>
                );
              })()}
            </div>
          )}

          {/* Guess input + actions */}
          {!result ? (
            <form onSubmit={submit} className="flex flex-col sm:flex-row gap-2 mb-3" data-testid="gtt-form">
              <input
                value={guess}
                onChange={(e) => setGuess(e.target.value)}
                placeholder="Your guess… e.g. Steve McNair"
                data-testid="gtt-input"
                className="flex-1 bg-titans-void border border-[rgba(138,141,143,0.3)] focus:border-titans-blue focus:outline-none px-4 py-3 font-body"
              />
              <button
                type="submit"
                disabled={submitting || !guess.trim()}
                data-testid="gtt-submit"
                className="bg-titans-red hover:bg-titans-red-hover disabled:opacity-50 px-5 py-3 font-cond uppercase tracking-wider text-sm inline-flex items-center justify-center gap-2"
              >
                <Send size={14} /> Lock In ({potentialPoints}pts)
              </button>
            </form>
          ) : (
            <button
              onClick={reset}
              data-testid="gtt-reset"
              className="bg-titans-elev hover:bg-titans-blue/30 text-titans-blue border border-titans-blue/40 px-5 py-3 font-cond uppercase tracking-wider text-sm inline-flex items-center gap-2"
            >
              <RotateCcw size={14} /> Replay
            </button>
          )}

          {/* Reveal / give-up */}
          {!result && (
            <div className="flex items-center justify-between flex-wrap gap-3 pt-3 border-t border-[rgba(138,141,143,0.15)]">
              <div className="text-titans-silver text-xs font-cond uppercase tracking-wider">
                Clue {used} of {cluesTotal} revealed
              </div>
              <div className="flex gap-2">
                <button
                  onClick={revealNext}
                  disabled={used >= cluesTotal}
                  data-testid="gtt-next-clue"
                  className="border border-titans-blue text-titans-blue hover:bg-titans-blue/10 disabled:opacity-40 disabled:cursor-not-allowed px-4 py-2 font-cond uppercase tracking-wider text-xs inline-flex items-center gap-1.5"
                >
                  <Lightbulb size={12} /> Next Clue
                </button>
                <button
                  onClick={giveUp}
                  data-testid="gtt-giveup"
                  className="border border-titans-silver/40 text-titans-silver hover:text-titans-red hover:border-titans-red px-4 py-2 font-cond uppercase tracking-wider text-xs inline-flex items-center gap-1.5"
                >
                  <Eye size={12} /> Give Up
                </button>
              </div>
            </div>
          )}

          {/* Footer trophy line */}
          <div className="mt-6 flex items-center justify-center gap-2 text-titans-silver text-[10px] font-cond uppercase tracking-wider">
            <Trophy size={11} className="text-titans-red" />
            New player every UTC day · Built for true Two-Tone Blue Nation
          </div>
        </>
      )}
    </div>
  );
}
